// src/utils/workforceCsv.js
//
// The organizational leg's file format, in one place: parse a CSV (quoted
// fields included, since unit names carry commas), map header spellings to
// the five columns, resolve metric names to dictionary ids, check groups
// against the organization's own list, and build the payload that
// ingestCorporateMetrics takes. Also the templates a client downloads to fill
// in. Used by the console's Data Intake tab and the super-admin uploader, so
// both validate identically.
//
// Two layouts are read:
//   long: one figure per line (date, group, source, metric, value);
//   wide: one row per group and period, one column per figure, the header
//         naming each figure by dictionary id or alias, or through a mapping
//         the organization saved the last time it sent a file in its own
//         words (organizations/{id}.intakeMappings, see metricDefinitions.js).
// A wide file is melted into long rows before the same checks run, so the
// payload is identical whichever way the file came in.

import { resolveMetricId, METRIC_BY_ID, METRICS, packFor } from '../data/workforceDictionary';

export const COLUMNS = ['date', 'group', 'source', 'metric', 'value'];

// Header spellings accepted for each column (normalised: lower case, spaces to _).
// `notes` is only so a remarks column in a wide file is left out quietly.
const HEADER_ALIASES = {
    date:   ['date', 'period_end', 'period', 'as_of', 'asof', 'month', 'timestamp', 'reporting_period'],
    // A bare "name" column is the row's label (a school, a unit), not a figure name: metric names come as metric, figure, measure, kpi or indicator.
    group:  ['group', 'group_id', 'unit', 'team', 'department', 'dept', 'division', 'squad', 'cohort', 'detachment', 'base', 'site', 'school', 'school_name', 'location', 'region', 'branch', 'office', 'facility', 'station', 'name'],
    source: ['source', 'system', 'signal_source', 'origin', 'from'],
    metric: ['metric', 'metric_name', 'metric_id', 'figure', 'measure', 'kpi', 'indicator'],
    value:  ['value', 'metric_value', 'amount', 'number', 'figure_value', 'result'],
    notes:  ['notes', 'note', 'comment', 'comments', 'remarks']
};

// Header and group normalisation: lower case, spaces and hyphens to _, nothing
// else kept. Saved column mappings are keyed by this, so metricDefinitions.js
// imports it rather than carrying its own copy.
const norm = (s) => String(s || '').trim().toLowerCase().replace(/^﻿/, '').replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '').replace(/^_+|_+$/g, '');
export const normHeader = norm;

const roleOf = (n) => Object.keys(HEADER_ALIASES).find((role) => HEADER_ALIASES[role].includes(n)) || null;

// Group spellings that mean organization-wide.
const ALL_WORDS = new Set(['all', 'organization', 'org_wide', 'orgwide', 'total']);

export function detectDelimiter(text) {
    const first = String(text || '').split(/\r?\n/).find((l) => l.trim()) || '';
    const counts = { ',': (first.match(/,/g) || []).length, ';': (first.match(/;/g) || []).length, '\t': (first.match(/\t/g) || []).length };
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][1] > 0 ? Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0] : ',';
}

// RFC 4180-style parse: quoted fields, doubled quotes, CR/LF line ends.
export function parseCsv(text, delimiter = ',') {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    const s = String(text || '').replace(/^﻿/, '');
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (inQuotes) {
            if (c === '"') {
                if (s[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
            } else field += c;
        } else if (c === '"') {
            inQuotes = true;
        } else if (c === delimiter) {
            row.push(field); field = '';
        } else if (c === '\n' || c === '\r') {
            if (c === '\r' && s[i + 1] === '\n') i++;
            row.push(field); field = '';
            rows.push(row); row = [];
        } else field += c;
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows.filter((r) => r.some((v) => String(v).trim() !== ''));
}

// A period end from what people type: YYYY-MM-DD as is, YYYY-MM as the last
// day of that month, an ISO timestamp cut to its date, else whatever Date parses.
export function parsePeriodEnd(raw) {
    const s = String(raw || '').trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return Number.isNaN(Date.parse(s)) ? null : s;
    if (/^\d{4}-\d{2}$/.test(s)) {
        const [y, m] = s.split('-').map(Number);
        if (m < 1 || m > 12) return null;
        return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : new Date(t).toISOString().slice(0, 10);
}

export function parseNumber(raw) {
    const s = String(raw ?? '').replace(/[%$€£,\s]/g, '').replace(/^\((.*)\)$/, '-$1');
    if (!s) return null;
    const v = Number(s);
    return Number.isFinite(v) ? v : null;
}

// Two dictionary ids that share a word with an unknown metric name, for the error text.
function suggest(name) {
    const tokens = norm(name).split('_').filter((t) => t.length >= 4);
    if (!tokens.length) return '';
    const hits = METRICS.filter((m) => tokens.some((t) => m.id.includes(t) || norm(m.label).includes(t) || m.aliases.some((a) => norm(a).includes(t)))).slice(0, 2);
    return hits.map((m) => m.id).join(' or ');
}

// The last day of the previous month, as an ISO date: the default period end.
export function lastPeriodEnd(now = new Date()) {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0)).toISOString().slice(0, 10);
}

const EMPTY = { total: 0, ok: 0, withErrors: 0, withWarnings: 0, metrics: [], groups: [], dates: [] };

const summarise = (rows) => {
    const valid = rows.filter((r) => !r.errors.length);
    return {
        total: rows.length,
        ok: valid.length,
        withErrors: rows.length - valid.length,
        withWarnings: rows.filter((r) => r.warnings.length).length,
        metrics: [...new Set(valid.map((r) => r.metricId))],
        groups: [...new Set(valid.map((r) => r.group))],
        dates: [...new Set(valid.map((r) => r.date))].sort()
    };
};

// The organization's group as written in a cell, resolved to its own spelling:
// blank or an org-wide word means 'All'; a name not on the list is kept, with a
// warning, so it appears as its own row.
const resolveGroup = (raw, groups, groupIndex, warnings) => {
    const g = norm(raw);
    if (!g || ALL_WORDS.has(g)) return 'All';
    if (!groups.length) return raw;
    const known = groupIndex.get(g);
    if (known) return known;
    warnings.push(`"${raw}" is not one of the organization's groups; it will appear as its own row`);
    return raw;
};

// One entry per header cell: the column's role (date, group, source, notes,
// metric, value, or null), the metric it names when it is a figure column, and
// a status: matched (in the dictionary), mapped (through a saved mapping),
// ignored (a role column or a blank header) or unknown (not sent until mapped).
// A saved mapping wins over the dictionary: it is a decision someone made.
function describeColumns(headerCells, mappings = {}, editionId = null) {
    return (headerCells || []).map((raw, index) => {
        const header = String(raw ?? '').replace(/^﻿/, '').trim();
        const n = norm(header);
        const role = roleOf(n);
        const base = { index, header, norm: n, role, metricId: null, status: 'ignored' };
        if (!n || role) return base;
        const mapped = mappings && mappings[n];
        if (mapped && METRIC_BY_ID[mapped]) return { ...base, metricId: mapped, status: 'mapped' };
        const id = resolveMetricId(header, editionId);
        if (id) return { ...base, metricId: id, status: 'matched' };
        return { ...base, status: 'unknown' };
    });
}

// Which way a file is laid out, from its header cells: 'long' when a metric
// column or a value column is recognised (one without the other is still a
// long file, and the missing column is reported, rather than a wide file that
// quietly drops the metric column), 'wide' when neither is present and at
// least one header names a figure (in the dictionary, or through a saved
// mapping keyed by normHeader), else 'unknown'.
export function detectLayout(headerCells, mappings = {}, editionId = null) {
    const cols = describeColumns(headerCells, mappings, editionId);
    const has = (role) => cols.some((c) => c.role === role);
    if (has('metric') || has('value')) return 'long';
    return cols.some((c) => c.metricId) ? 'wide' : 'unknown';
}

const UNKNOWN_LAYOUT = 'No figure column was recognised. One figure per line needs date, metric and value columns. One column per figure needs at least one header that names a figure by its dictionary id or alias; the other columns can be mapped once the file is read.';

// A wide table melted into long rows: one row per figure column per line,
// blank cells skipped. The date column is optional (periodEnd stands in when
// the file has none), so are group (All) and source (the source option, then
// 'CSV upload'). Only columns that resolved to a metric are read.
function meltWide(table, cols, { groups, periodEnd, source }) {
    const first = (role) => cols.find((c) => c.role === role) || null;
    const dateCol = first('date');
    const groupCol = first('group');
    const sourceCol = first('source');
    const figureCols = cols.filter((c) => c.metricId);
    const fallbackDate = dateCol ? null : parsePeriodEnd(periodEnd);
    const fallbackSource = String(source || '').trim() || 'CSV upload';
    const groupIndex = new Map(groups.map((g) => [norm(g), g]));
    const rows = [];
    table.slice(1).forEach((cells, i) => {
        const line = i + 2;
        const cell = (c) => (c ? String(cells[c.index] ?? '').trim() : '');

        const rawDate = cell(dateCol);
        const dateErrors = [];
        let date = rawDate;
        if (dateCol) {
            const d = parsePeriodEnd(rawDate);
            if (!d) dateErrors.push(rawDate ? `"${rawDate}" is not a date (use YYYY-MM-DD, or YYYY-MM for a whole month)` : `no date in the ${dateCol.header} column`);
            else date = d;
        } else if (fallbackDate) date = fallbackDate;
        else dateErrors.push('no period end: the file has no date column, so set a period end for it (YYYY-MM-DD)');

        const groupWarnings = [];
        const group = resolveGroup(cell(groupCol), groups, groupIndex, groupWarnings);
        const src = cell(sourceCol) || fallbackSource;

        figureCols.forEach((c) => {
            const raw = String(cells[c.index] ?? '').trim();
            if (!raw) return;
            const r = { line, column: c.header, columnIndex: c.index, date, group, source: src, metric: c.header, value: raw, metricId: c.metricId, label: METRIC_BY_ID[c.metricId].label, errors: [...dateErrors], warnings: [...groupWarnings] };
            const v = parseNumber(raw);
            if (v === null) r.errors.push(`"${raw}" is not a number`); else r.value = v;
            rows.push(r);
        });
    });
    return rows;
}

// Parse and check a file. Returns { fileErrors, fileWarnings, rows, summary,
// delimiter, layout, columns }; a row carries errors (blocks sending) and
// warnings (sent, but worth a look). Options: the organization's `groups`;
// `mappings` { [normHeader]: metricId } for wide-file headers in the
// organization's own words (a mapping to an id not in the dictionary is
// ignored); `periodEnd` (YYYY-MM-DD) for a wide file with no date column;
// `source` for a file with no source column. Rows melted from a wide file keep
// `line` as the source line and gain `column`, the header they came from, and
// `columnIndex`, its position in the header (two columns with the same header
// are told apart by it).
export function validateRows(text, { groups = [], mappings = {}, periodEnd = null, source = '', editionId = null } = {}) {
    const delimiter = detectDelimiter(text);
    const table = parseCsv(text, delimiter);
    const columns = table.length ? describeColumns(table[0], mappings, editionId) : [];
    const layout = table.length ? detectLayout(table[0], mappings, editionId) : 'unknown';
    const out = (fileErrors, rows, fileWarnings = []) => ({ fileErrors, fileWarnings, rows, summary: rows.length ? summarise(rows) : EMPTY, delimiter, layout, columns });

    if (table.length < 2) return out(['The file has no data rows under its header.'], []);
    if (layout === 'unknown') return out([UNKNOWN_LAYOUT], []);

    if (layout === 'wide') {
        const rows = meltWide(table, columns, { groups, periodEnd, source });
        const fileWarnings = [];
        const unknown = columns.filter((c) => c.status === 'unknown');
        if (unknown.length) fileWarnings.push(`${unknown.length} column${unknown.length === 1 ? '' : 's'} not recognised and left out: ${unknown.map((c) => c.header).join(', ')}. Map ${unknown.length === 1 ? 'it' : 'them'} under Columns to send ${unknown.length === 1 ? 'it' : 'them'}.`);
        // Two figure columns on one id (a header repeated, or a saved mapping
        // pointing a column at an id another header already matches) both
        // become rows for the same date, group and metric. Say so; identical
        // headers are told apart by their position.
        const byMetric = new Map();
        columns.filter((c) => c.metricId).forEach((c) => { if (!byMetric.has(c.metricId)) byMetric.set(c.metricId, []); byMetric.get(c.metricId).push(c); });
        byMetric.forEach((cols, id) => {
            if (cols.length < 2) return;
            const distinct = new Set(cols.map((c) => c.header)).size === cols.length;
            const names = cols.map((c) => (distinct ? c.header : `${c.header} (column ${c.index + 1})`));
            const list = `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
            const word = cols.length === 2 ? 'both' : 'all';
            fileWarnings.push(`Columns ${list} ${word} map to ${METRIC_BY_ID[id].label}; ${word} will be sent.`);
        });
        if (!columns.some((c) => c.role === 'group')) {
            const linesByDate = new Map();
            rows.forEach((r) => { if (!linesByDate.has(r.date)) linesByDate.set(r.date, new Set()); linesByDate.get(r.date).add(r.line); });
            if ([...linesByDate.values()].some((s) => s.size > 1)) fileWarnings.push('There is no group column, and more than one row shares a period end, so those figures all land organization-wide. Add a group column if each row is a unit.');
        }
        return out([], rows, fileWarnings);
    }

    // Long layout: one figure per line.
    const header = table[0].map(norm);
    const idx = {};
    Object.entries(HEADER_ALIASES).forEach(([col, names]) => {
        const i = header.findIndex((h) => names.includes(h));
        if (i >= 0) idx[col] = i;
    });
    const missing = ['date', 'metric', 'value'].filter((c) => idx[c] === undefined);
    if (missing.length) {
        return out([`Missing column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. The header needs date, metric and value (group and source are optional); spellings such as period_end, unit or figure are accepted.`], []);
    }

    const groupIndex = new Map(groups.map((g) => [norm(g), g]));
    const rows = table.slice(1).map((cells, i) => {
        const get = (c) => (idx[c] === undefined ? '' : String(cells[idx[c]] ?? '').trim());
        const r = { line: i + 2, date: get('date'), group: get('group'), source: get('source'), metric: get('metric'), value: get('value'), metricId: null, label: null, errors: [], warnings: [] };

        const d = parsePeriodEnd(r.date);
        if (!d) r.errors.push(`"${r.date}" is not a date (use YYYY-MM-DD, or YYYY-MM for a whole month)`); else r.date = d;

        r.metricId = resolveMetricId(r.metric, editionId);
        if (!r.metricId) {
            const s = suggest(r.metric);
            r.errors.push(`"${r.metric}" is not in the workforce dictionary${s ? ` (did you mean ${s}?)` : ''}`);
        } else r.label = METRIC_BY_ID[r.metricId].label;

        const v = parseNumber(r.value);
        if (v === null) r.errors.push(`"${r.value}" is not a number`); else r.value = v;

        r.group = resolveGroup(r.group, groups, groupIndex, r.warnings);
        if (!r.source) r.source = 'CSV upload';
        return r;
    });

    return out([], rows);
}

// Several parsed files as one summary, for a combined preview: counts added
// up, metrics, groups and dates merged (dates sorted), plus how many files and
// how many of them could not be read at all.
export function summariseFiles(parsedList = []) {
    const list = (parsedList || []).filter(Boolean);
    const metrics = new Set();
    const groups = new Set();
    const dates = new Set();
    const s = { total: 0, ok: 0, withErrors: 0, withWarnings: 0, metrics: [], groups: [], dates: [], files: list.length, filesWithErrors: 0 };
    list.forEach((p) => {
        const x = p.summary || EMPTY;
        s.total += x.total || 0;
        s.ok += x.ok || 0;
        s.withErrors += x.withErrors || 0;
        s.withWarnings += x.withWarnings || 0;
        (x.metrics || []).forEach((m) => metrics.add(m));
        (x.groups || []).forEach((g) => groups.add(g));
        (x.dates || []).forEach((d) => dates.add(d));
        if (p.fileErrors && p.fileErrors.length) s.filesWithErrors += 1;
    });
    s.metrics = [...metrics];
    s.groups = [...groups];
    s.dates = [...dates].sort();
    return s;
}

// A payload cut into callable-sized requests, in order.
export function splitBatches(payload, size = 400) {
    const n = Math.max(1, Math.floor(Number(size) || 400));
    const list = Array.isArray(payload) ? payload : [];
    const out = [];
    for (let i = 0; i < list.length; i += n) out.push(list.slice(i, i + n));
    return out;
}

// What ingestCorporateMetrics takes, from the rows without errors.
export const buildPayload = (rows) => rows.filter((r) => !r.errors.length).map((r) => ({
    date: r.date, groupId: r.group || 'All', source: r.source || 'CSV upload', metricName: r.metricId, metricValue: r.value
}));

const csvField = (v) => {
    const s = String(v ?? '');
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// A template for the sector: one row per pack metric per group (org-wide
// when the organization has no groups), plus org-wide headcount and labour
// cost, with the period end filled in and the values left blank.
export function templateCsv({ editionId, groups = [], periodEnd = lastPeriodEnd() } = {}) {
    const pack = packFor(editionId);
    const lines = [COLUMNS.join(',')];
    const scopes = groups.length ? groups : ['All'];
    pack.forEach((m) => scopes.forEach((g) => lines.push([periodEnd, csvField(g), '', m.id, ''].join(','))));
    ['headcount', 'loaded_labour_cost'].forEach((id) => {
        if (!pack.some((m) => m.id === id)) lines.push([periodEnd, 'All', '', id, ''].join(','));
    });
    return `${lines.join('\r\n')}\r\n`;
}

const fileSlug = (orgName) => String(orgName || 'organization').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const templateFilename = (orgName, editionId) => `${fileSlug(orgName)}-${editionId || 'workforce'}-figures.csv`;

// The same template the other way round: one row per group (org-wide when the
// organization has no groups), one column per pack metric id, the period end
// filled in and the values left blank.
export function wideTemplateCsv({ editionId, groups = [], periodEnd = lastPeriodEnd() } = {}) {
    const pack = packFor(editionId);
    const header = ['date', 'group', 'source', ...pack.map((m) => m.id)];
    const scopes = groups.length ? groups : ['All'];
    const lines = [header.join(','), ...scopes.map((g) => [periodEnd, csvField(g), '', ...pack.map(() => '')].join(','))];
    return `${lines.join('\r\n')}\r\n`;
}

export const wideTemplateFilename = (orgName, editionId) => `${fileSlug(orgName)}-${editionId || 'workforce'}-figures-wide.csv`;
